import { apiClient } from "./apiService";

// Tournament API Service
export const tournamentService = {
  // Get all tournaments with filtering
  getTournaments: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key] !== undefined && filters[key] !== "") {
          params.append(key, filters[key]);
        }
      });
      const response = await apiClient.get(`/tournaments?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch tournaments");
    }
  },

  // Get tournament by ID
  getTournamentById: async (tournamentId) => {
    try {
      const response = await apiClient.get(`/tournaments/${tournamentId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch tournament");
    }
  },

  // Create new tournament
  createTournament: async (tournamentData) => {
    try {
      const response = await apiClient.post(`/tournaments`, tournamentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to create tournament");
    }
  },

  // Update tournament
  updateTournament: async (tournamentId, updateData) => {
    try {
      const response = await apiClient.put(`/tournaments/${tournamentId}`, updateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to update tournament");
    }
  },

  // Delete tournament
  deleteTournament: async (tournamentId) => {
    try {
      const response = await apiClient.delete(`/tournaments/${tournamentId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to delete tournament");
    }
  },

  // Register for tournament
  registerForTournament: async (tournamentId, registrationData) => {
    try {
      const response = await apiClient.post(
        `/tournaments/${tournamentId}/register`,
        registrationData
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to register for tournament");
    }
  },

  // Pay my entry fee: { data: { status: 'payment_required', checkoutUrl, paymentId } }
  payEntryFee: async (tournamentId) => {
    try {
      const response = await apiClient.post(`/tournaments/${tournamentId}/pay`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Could not start the payment");
    }
  },

  // Get tournament registrations (for organizers)
  getTournamentRegistrations: async (tournamentId, status = null) => {
    try {
      const params = status ? `?status=${status}` : "";
      const response = await apiClient.get(
        `/tournaments/${tournamentId}/registrations${params}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch registrations");
    }
  },

  // Get user's tournament registrations
  getUserTournamentRegistrations: async () => {
    try {
      const response = await apiClient.get(`/tournaments/user/my-registrations`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch user registrations");
    }
  },

  // Open tournament registration
  openTournamentRegistration: async (tournamentId) => {
    try {
      const response = await apiClient.patch(
        `/tournaments/${tournamentId}/open-registration`,
        {}
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to open tournament registration"
      );
    }
  },

  // Close tournament registration
  closeRegistration: async (tournamentId) => {
    try {
      const response = await apiClient.patch(`/tournaments/${tournamentId}/close-registration`, {});
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to close registration");
    }
  },

  // Start tournament (registration_closed → in_progress)
  startTournament: async (tournamentId) => {
    try {
      const response = await apiClient.patch(`/tournaments/${tournamentId}/start`, {});
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to start tournament");
    }
  },

  // Complete tournament
  completeTournament: async (tournamentId) => {
    try {
      const response = await apiClient.patch(`/tournaments/${tournamentId}/complete`, {});
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to complete tournament");
    }
  },

  // Cancel tournament
  cancelTournament: async (tournamentId) => {
    try {
      const response = await apiClient.patch(`/tournaments/${tournamentId}/cancel`, {});
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to cancel tournament");
    }
  },

  /**
   * Generate match schedule for a tournament based on its tournament_type.
   * @param {number} tournamentId
   * @param {Object} options
   * @param {boolean} [options.clearExisting=false] - Delete existing matches first
   * @param {boolean} [options.shuffle=false]       - Randomise participant seeding order
   */
  generateSchedule: async (tournamentId, options = {}) => {
    try {
      const response = await apiClient.post(
        `/tournaments/${tournamentId}/generate-schedule`,
        {
          clearExisting: options.clearExisting ?? false,
          shuffle: options.shuffle ?? false,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to generate schedule");
    }
  },

  // Get tournaments by sport
  getTournamentsBySport: async (sportId, filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await apiClient.get(
        `/tournaments/by-sport/${sportId}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch tournaments by sport");
    }
  },

  // Get tournaments by status
  getTournamentsByStatus: async (status, filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await apiClient.get(
        `/tournaments/status/${status}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch tournaments by status");
    }
  },

  // Invite a team to a tournament
  inviteTeamToTournament: async (tournamentId, teamId, message) => {
    try {
      const response = await apiClient.post(`/tournaments/${tournamentId}/invite-team`, { team_id: teamId, message });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to invite team");
    }
  },

  // Invite a team to a league
  inviteTeamToLeague: async (leagueId, teamId, message) => {
    try {
      const response = await apiClient.post(`/leagues/${leagueId}/invite-team`, { team_id: teamId, message });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to invite team to league");
    }
  },

  // Request to join a public league (team owner → host)
  requestToJoinLeague: async (leagueId, teamId, message) => {
    try {
      const response = await apiClient.post(`/leagues/${leagueId}/join-request`, { team_id: teamId, message });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to send join request");
    }
  },

  // Get invitations / join-requests for a league (host only)
  getLeagueInvitations: async (leagueId, status = "pending") => {
    try {
      const response = await apiClient.get(`/leagues/${leagueId}/invitations?status=${status}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch league invitations");
    }
  },

  // Host responds to an invitation or join-request
  // ── Singles / doubles leagues (participant_format 'individual' / 'pair') ──
  invitePlayerToLeague: async (leagueId, userId, message) => {
    try {
      const response = await apiClient.post(`/leagues/${leagueId}/invite-team`, { user_id: userId, message });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to invite player to league");
    }
  },

  invitePairToLeague: async (leagueId, playerIds, message) => {
    try {
      const response = await apiClient.post(`/leagues/${leagueId}/invite-team`, { player_ids: playerIds, message });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to invite pair to league");
    }
  },

  requestToJoinLeagueAsPlayer: async (leagueId, message) => {
    try {
      const response = await apiClient.post(`/leagues/${leagueId}/join-request`, { message });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to send join request");
    }
  },

  requestToJoinLeagueAsPair: async (leagueId, partnerUserId, message) => {
    try {
      const response = await apiClient.post(`/leagues/${leagueId}/join-request`, { partner_user_id: partnerUserId, message });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to send join request");
    }
  },

  getMyLeagueInvitation: async (leagueId) => {
    try {
      const response = await apiClient.get(`/leagues/${leagueId}/my-invitation`);
      return response.data?.data || null;
    } catch {
      return null;
    }
  },

  // Doubles: requests from players who asked me to be their partner (LG-B16)
  getLeaguePartnerRequests: async (leagueId) => {
    try {
      const response = await apiClient.get(`/leagues/${leagueId}/partner-requests`);
      return response.data?.data || [];
    } catch {
      return [];
    }
  },

  respondToLeaguePartnerRequest: async (leagueId, pairTeamId, action) => {
    try {
      const response = await apiClient.put(`/leagues/${leagueId}/partner-requests/${pairTeamId}/respond`, { action });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to respond to the partner request");
    }
  },

  respondToLeaguePlayerInvitation: async (leagueId, invitationId, action) => {
    try {
      const response = await apiClient.put(`/leagues/${leagueId}/player-invitations/${invitationId}/respond`, { action });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to respond to invitation");
    }
  },

  hostRespondToLeagueInvitation: async (leagueId, invitationId, action) => {
    try {
      const response = await apiClient.put(`/leagues/${leagueId}/invitations/${invitationId}/respond`, { action });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to respond to invitation");
    }
  },

  // Get tournament/league invitations for teams I own
  getMyTeamInvitations: async (status = "pending") => {
    try {
      const response = await apiClient.get(`/teams/tournament-invitations?status=${status}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch team invitations");
    }
  },

  // Accept or decline a tournament/league team invitation
  respondToTeamInvitation: async (invitationId, action) => {
    try {
      const response = await apiClient.put(`/teams/tournament-invitations/${invitationId}/respond`, { action });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to respond to invitation");
    }
  },
};

export default tournamentService;
